// 渲染层：对话气泡、经卷对照卡、会话列表、提示条 / toast / 长按菜单。
import { t } from "./i18n";
import type { ClientConfig, SessionSummary, Source, Turn } from "./types";

const CITE_MARK = /【(经卷出处溯源|經卷出處溯源)】/;

export function $<T extends HTMLElement = HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

// ---------- 对话区 ----------
export interface ThreadHandlers {
  onRegen: () => void;
  onExample: (text: string) => void;
  onChipClick: (chunkId: string) => void;
}

export function renderThread(turns: Turn[], h: ThreadHandlers): void {
  const box = $("messages");
  box.replaceChildren();

  if (!turns.length) {
    box.appendChild(renderHero());
    return;
  }
  const thread = el("div", "thread");
  turns.forEach((turn, i) => {
    const isLastAssistant = turn.role === "assistant" && i === turns.length - 1 && !turn.streaming;
    thread.appendChild(renderTurn(turn, isLastAssistant, h));
  });
  box.appendChild(thread);
  // 滚到底
  box.scrollTop = box.scrollHeight;
}

function renderHero(): HTMLElement {
  const hero = el("div", "empty-hero");
  hero.appendChild(el("div", "seal", "禪"));
  hero.appendChild(el("h2", undefined, t("empty_title")));
  hero.appendChild(el("p", undefined, t("empty_hint")));
  return hero;
}

function renderTurn(turn: Turn, isLastAssistant: boolean, h: ThreadHandlers): HTMLElement {
  const wrap = el("div", `turn ${turn.role}`);
  const bubble = el("div", "bubble");
  bubble.dataset.bubble = "1";
  bubble.dataset.turnId = turn.id;
  if (turn.streaming) bubble.classList.add("streaming");
  if (turn.role === "assistant" && turn.grounded === false) bubble.classList.add("ungrounded");

  if (turn.role === "assistant") {
    const m = turn.content.match(CITE_MARK);
    if (m && m.index !== undefined) {
      bubble.appendChild(el("span", undefined, turn.content.slice(0, m.index).trimEnd()));
      const cite = el("div", "cite-block", turn.content.slice(m.index));
      bubble.appendChild(cite);
    } else {
      bubble.appendChild(document.createTextNode(turn.content));
    }
  } else {
    bubble.textContent = turn.content;
  }
  wrap.appendChild(bubble);

  // 溯源 chips
  if (turn.role === "assistant" && turn.sources?.length) {
    const chips = el("div", "src-chips");
    turn.sources.forEach((s, idx) => {
      const chip = el("button", "src-chip", `〔${idx + 1}〕${s.citation}`);
      chip.type = "button";
      chip.dataset.chunk = s.chunk_id;
      chip.addEventListener("click", () => h.onChipClick(s.chunk_id));
      chips.appendChild(chip);
    });
    wrap.appendChild(chips);
  }

  // 末条 assistant：重新生成
  if (isLastAssistant) {
    const acts = el("div", "bubble-actions");
    const regen = el("button", "mini-btn", "↻ " + t("regen"));
    regen.type = "button";
    regen.addEventListener("click", h.onRegen);
    acts.appendChild(regen);
    wrap.appendChild(acts);
  }
  return wrap;
}

// 流式增量：仅更新最后一个气泡文本，避免整树重绘
export function patchStreamingBubble(turn: Turn): void {
  const bubble = document.querySelector<HTMLElement>(
    `.bubble[data-turn-id="${turn.id}"]`,
  );
  const box = $("messages");
  if (!bubble) {
    return;
  }
  bubble.replaceChildren();
  const m = turn.content.match(CITE_MARK);
  if (m && m.index !== undefined) {
    bubble.appendChild(el("span", undefined, turn.content.slice(0, m.index).trimEnd()));
    bubble.appendChild(el("div", "cite-block", turn.content.slice(m.index)));
  } else {
    bubble.appendChild(document.createTextNode(turn.content));
  }
  bubble.classList.toggle("streaming", !!turn.streaming);
  box.scrollTop = box.scrollHeight;
}

// ---------- 右栏 经卷对照 ----------
export function renderRefs(sources: Source[]): void {
  const list = $("refs-list");
  list.replaceChildren();
  if (!sources?.length) {
    list.appendChild(el("p", "refs-empty", t("refs_empty")));
    return;
  }
  const max = Math.max(...sources.map((s) => s.score || 0), 1e-9);
  sources.forEach((s, idx) => {
    const card = el("div", "ref-card");
    card.dataset.chunk = s.chunk_id;
    const head = el("div", "rc-head");
    head.appendChild(el("span", "rc-idx", String(idx + 1)));
    const meta = el("div");
    meta.style.flex = "1";
    meta.appendChild(el("div", "rc-cite", s.citation));
    if (s.cbeta_ref) meta.appendChild(el("div", "rc-ref", s.cbeta_ref));
    head.appendChild(meta);
    card.appendChild(head);
    card.appendChild(el("div", "rc-text", s.snippet));
    const bar = el("div", "rc-score");
    const fill = el("i");
    fill.style.width = `${Math.round(((s.score || 0) / max) * 100)}%`;
    bar.appendChild(fill);
    card.appendChild(bar);
    list.appendChild(card);
  });
}

export function focusRef(chunkId: string): void {
  const list = $("refs-list");
  list.querySelectorAll(".ref-card.focus").forEach((c) => c.classList.remove("focus"));
  const card = list.querySelector<HTMLElement>(`.ref-card[data-chunk="${cssEsc(chunkId)}"]`);
  if (card) {
    card.classList.add("focus");
    card.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }
}

// ---------- 左栏 会话列表 ----------
export function renderSessions(
  sessions: SessionSummary[],
  currentId: string,
  onSelect: (sid: string) => void,
  onDelete: (sid: string) => void,
): void {
  const nav = $("session-list");
  nav.replaceChildren();
  if (!sessions.length) {
    nav.appendChild(renderEmptySidebar());
    return;
  }
  sessions.forEach((s) => {
    const item = el("div", "session-item session-row" + (s.session_id === currentId ? " active" : ""));
    const main = el("button", "session-main");
    main.type = "button";
    main.appendChild(el("span", "s-title", s.title || t("new_chat").replace(/^＋\s*/, "")));
    main.appendChild(el("span", "s-meta", `${s.turns} ${t("session_count_suffix")} · ${fmtTime(s.last_ts)}`));
    main.addEventListener("click", () => onSelect(s.session_id));
    const del = el("button", "session-delete", "×");
    del.type = "button";
    del.title = t("delete_session");
    del.setAttribute("aria-label", t("delete_session"));
    del.addEventListener("click", () => {
      if (window.confirm(t("delete_session_confirm"))) onDelete(s.session_id);
    });
    item.appendChild(main);
    item.appendChild(del);
    nav.appendChild(item);
  });
}

function renderEmptySidebar(): HTMLElement {
  return el("p", "session-empty", t("session_empty"));
}

export function setSchoolBadge(school: string | null | undefined): void {
  const b = $("school-badge");
  if (school) {
    b.textContent = `${t("school_prefix")} · ${school}`;
    b.hidden = false;
  } else {
    b.hidden = true;
  }
}

export function setCorpusStats(cfg: ClientConfig): void {
  const c = cfg.corpus;
  const chars = Number(c.chars || 0).toLocaleString();
  $("corpus-stats").textContent = c.indexed
    ? `${t("corpus_doc_label")} ${c.documents} ${t("corpus_doc_unit")} · ${t("corpus_word_label")} ${chars} 字`
    : t("no_corpus");
  const badge = $("llm-badge");
  badge.textContent = "";
  badge.hidden = true;
  $("brand-name").textContent = "Zen-RAG";
}

// ---------- 提示条 / toast / 菜单 ----------
let toastTimer: number | null = null;
export function toast(msg: string): void {
  const el0 = $("toast");
  el0.textContent = msg;
  el0.hidden = false;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (el0.hidden = true), 2200);
}

export function showNetBanner(msg: string | null): void {
  const b = $("net-banner");
  if (msg) {
    b.textContent = msg;
    b.hidden = false;
  } else {
    b.hidden = true;
  }
}

export function showCtxMenu(x: number, y: number): void {
  const menu = $("ctx-menu");
  menu.hidden = false;
  const w = menu.offsetWidth || 160;
  const h = menu.offsetHeight || 96;
  menu.style.left = `${Math.min(x, window.innerWidth - w - 8)}px`;
  menu.style.top = `${Math.min(y, window.innerHeight - h - 8)}px`;
}
export function hideCtxMenu(): void {
  $("ctx-menu").hidden = true;
}

// ---------- 工具 ----------
function fmtTime(ts: number): string {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 86400000 && d.getDate() === new Date().getDate())
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
function pad(n: number): string {
  return n < 10 ? "0" + n : String(n);
}
function cssEsc(s: string): string {
  return s.replace(/["\\]/g, "\\$&");
}

export function applyStaticText(): void {
  ($("input") as HTMLTextAreaElement).placeholder = t("placeholder");
  $("btn-new").textContent = t("new_chat");
  $("btn-send").textContent = t("send");
  $("topbar-title").textContent = t("topbar_title");
  $("voice-hint").textContent = t("voice_hint");
  document.querySelector(".brand-text small")!.textContent = t("brand_sub");
  $("btn-menu").setAttribute("aria-label", t("menu_label"));
  $("btn-refs").setAttribute("aria-label", t("refs_label"));
  $("btn-refs-close").setAttribute("aria-label", t("refs_close"));
  document.querySelector(".refs-head h2")!.textContent = t("refs_title");
  document.querySelector('#ctx-menu [data-act="copy"]')!.textContent = t("copy");
  document.querySelector('#ctx-menu [data-act="share"]')!.textContent = t("share");
}
